// -----------------------------------------------------------------------------
// i18n — French (default) + English. Language is derived from the URL:
// "/en" and "/en/..." → English, everything else → French.
// UI strings live here; per-item data translations (excursions, offers,
// add-ons, testimonials) live as `en: {...}` blocks in site.js.
// -----------------------------------------------------------------------------
import { useLocation } from "react-router-dom";

export const I18N = {
    fr: {
        nav: {
            accueil: "Accueil",
            experiences: "Expériences",
            about: "À propos",
            offres: "Nos offres",
            addons: "Add-ons",
            contact: "Contact",
            quote: "Devis gratuit",
        },
        hero: {
            overline: "Conciergerie d'expériences haut de gamme",
            titleA: "Vivez Maurice",
            titleB: "autrement",
            side: "Île Maurice — Estd 2026",
            subtitle:
                "Excursions en speedboat, sorties pêche et parachute au départ de Trou d'Eau Douce. Les îles de l'Est, les lagons et les cascades, organisés pour vous, comme pour personne d'autre.",
            ctaPrimary: "Organiser mon expérience",
            ctaSecondary: "Voir les expériences",
            scroll: "Découvrez l'expérience",
            waMessage:
                "Bonjour Exotik Trips, je souhaite organiser une expérience à Maurice.",
            bgAlt: "Lagon Maurice au coucher du soleil",
        },
        experiences: {
            kicker: "— Catalogue",
            titleA: "Des sorties en mer pensées ",
            titleB: "pour vous.",
            intro: "Excursions en bateau privé, pêche, parachute : choisissez votre journée au départ de Trou d'Eau Douce, nous nous chargeons du reste.",
            cardKicker: "Sortie phare · Est de Maurice",
            cardTitle: "Sorties bateau",
            cardText: "5 îles de l'Est, sortie pêche, parachute ascensionnel : au départ de Trou d'Eau Douce.",
            flagLabel: "Excursion phare",
            flagLine: "5 îles de l'Est en bateau privé : Rs 18 000 pour 4 personnes, déjeuner inclus.",
            cta: "Voir les excursions",
        },
        about: {
            kicker: "À propos",
            titleA: "L'île Maurice,",
            titleB: "côté secret",
            brand: "Exotik Trips Moris",
            p1: " vous fait découvrir l'Île Maurice autrement à travers des excursions uniques, des expériences en mer et des activités adaptées à vos envies.",
            p2: "Grâce à nos différents partenaires, nous vous proposons une sélection d'activités et d'expériences soigneusement choisies pour rendre votre séjour inoubliable.",
            p3: "De votre arrivée à l'aéroport jusqu'à votre hébergement, nous vous accompagnons pour rendre votre séjour simple, confortable et inoubliable.",
            p4: "Nous proposons également des packs d'excursions, des guides d'activités ainsi qu'un service de courses essentielles préparées avant votre arrivée pour vous permettre de profiter pleinement de vos vacances.",
            tags: [
                "Excursions",
                "Transferts aéroport",
                "Hébergement",
                "Courses avant arrivée",
                "Guides sur place",
            ],
        },
        whyus: {
            kicker: "Pourquoi nous choisir",
            titleA: "Le luxe, c'est",
            titleB: "de ne penser à rien",
            intro: "Exotik Trips, bien plus qu'une agence : une conciergerie pensée autour de vous. Chaque détail est imaginé, coordonné et anticipé pour que votre seule mission soit de profiter.",
            quote: "« L'expérience commence bien avant votre arrivée. »",
            reasons: [
                {
                    id: "premium",
                    title: "Premium",
                    text: "Prestataires triés sur le volet, équipements haut de gamme, finitions soignées jusqu'au dernier détail.",
                },
                {
                    id: "local",
                    title: "100% Local",
                    text: "Nous vivons Maurice. Spots secrets, contacts directs, savoir-faire transmis génération après génération.",
                },
                {
                    id: "stressfree",
                    title: "Sans stress",
                    text: "Nous coordonnons tout — vous profitez. Plan B inclus, météo surveillée, un humain au bout du fil.",
                },
                {
                    id: "surmesure",
                    title: "Sur mesure",
                    text: "Chaque expérience est dessinée autour de vos envies, de votre rythme, de votre histoire.",
                },
            ],
        },
        offers: {
            kicker: "Nos offres",
            titleA: "Trois manières de",
            titleB: "vivre",
            titleC: "Maurice.",
            intro: "Choisissez le niveau d'accompagnement. Le paiement des activités s'effectue directement auprès des prestataires.",
            perOrder: "/commande",
            cta: "Demander cette offre",
            disclaimer:
                "Les tarifs affichés couvrent les frais de conciergerie. Les prestations sont facturées séparément par les partenaires.",
            waMessage: (name, price) =>
                `Bonjour, je suis intéressé(e) par l'offre ${name} (${price}).`,
        },
        addons: {
            kicker: "Personnaliser",
            titleA: "Les détails qui",
            titleB: "font la magie",
            intro: "Sélectionnez vos add-ons, nous orchestrons le reste. Champagne au coucher du soleil, gâteau surprise sur la plage, photographe pour figer l'instant — la touche finale qui transforme un moment en souvenir.",
            selection: "Votre sélection",
            empty: "Choisissez vos add-ons",
            count: (n) =>
                `${n} élément${n > 1 ? "s" : ""} ajouté${n > 1 ? "s" : ""}`,
            cta: "Ajouter à mon expérience →",
            waEmpty:
                "Bonjour Exotik Trips, j'aimerais personnaliser mon expérience avec des add-ons.",
            waWith: (names) =>
                `Bonjour Exotik Trips, j'aimerais ajouter à mon expérience : ${names}.`,
        },
        ctaband: {
            kicker: "Maurice vous attend",
            titleA: "Et si demain",
            titleB: "était",
            titleC: "inoubliable",
            cta: "Organiser mon expérience",
            waMessage: "Bonjour, je veux organiser mon expérience à Maurice.",
        },
        contact: {
            kicker: "Contact",
            titleA: "Parlons de votre",
            titleB: "prochaine évasion",
            whatsappLabel: "WhatsApp — le plus rapide",
            phoneLabel: "Téléphone",
            emailLabel: "Email",
            delayLabel: "Délai de réponse",
            delayValue: "Sous 24h, du lundi au samedi",
            badge: "Réponse sous 24h",
            waMessage:
                "Bonjour Exotik Trips, j'aimerais discuter d'une expérience.",
            nameLabel: "Nom complet *",
            namePh: "Votre nom",
            emailLabel: "Email *",
            emailPh: "vous@email.com",
            phoneLabel: "Téléphone",
            phonePh: "+230 ou autre",
            experienceLabel: "Expérience souhaitée",
            choose: "— Choisir —",
            messageLabel: "Votre message",
            messagePh: "Dates, nombre de personnes, envies particulières…",
            replyNoteA: "Réponse rapide sous ",
            replyNoteB: "24h",
            submit: "Envoyer ma demande",
            sending: "Envoi…",
            expOptions: [
                "Excursion 5 Îles de l'Est",
                "Sortie de pêche",
                "Parachute ascensionnel",
                "Autre demande",
            ],
            successKicker: "Demande reçue",
            successTitleA: "Votre demande a bien été",
            successTitleB: "prise en compte.",
            successText:
                "Nous revenons vers vous sous 24h directement par WhatsApp ou email. Pour une réponse plus rapide, n'hésitez pas à nous écrire directement.",
            successWa: "Écrire sur WhatsApp",
            successWaMessage:
                "Bonjour, j'ai envoyé une demande via le formulaire du site.",
            successNew: "Nouvelle demande",
            errorRequired: "Nom et email requis",
            errorGeneric:
                "Une erreur est survenue. Réessayez ou utilisez WhatsApp.",
        },
        footer: {
            description:
                "Conciergerie d'expériences haut de gamme à l'île Maurice. Du lagon turquoise aux montagnes du Sud, nous orchestrons votre évasion.",
            explore: "Explorer",
            contact: "Contact",
            legal: "Exotik Trips agit en tant que service de conciergerie et intermédiaire. Les prestations sont réalisées par des partenaires indépendants. Le paiement des activités est effectué directement auprès des prestataires.",
            rights: "Tous droits réservés",
            madeIn: "Made with love · Île Maurice",
            admin: "Espace Pro",
        },
        waFloat: {
            label: "Discuter avec nous",
            aria: "Discuter avec nous sur WhatsApp",
            message:
                "Bonjour Exotik Trips, j'aimerais organiser une expérience.",
        },
        boat: {
            modalKicker: "Sorties bateau",
            modalTitleA: "Le lagon",
            modalTitleB: "turquoise",
            modalTitleC: "sous toutes ses formes.",
            close: "Fermer",
            backHome: "Retour à l'accueil",
            pageKicker: "Sorties bateau · Est de Maurice",
            tabsAria: "Sélectionner une excursion",
            factDeparture: "Départ",
            factDuration: "Durée",
            factEnd: "Fin",
            factBase: "Base",
            pax: "pers.",
            yourDay: "Votre journée",
            seo5ilesH2a: "Les 5 îles de l'Est de Maurice au départ de Trou d'Eau Douce",
            seo5ilesPa: "Une journée en bateau privé pour explorer les cinq joyaux du lagon de l'Est : Île au Phare (Île aux Fouquets), Île de la Passe, Île aux Marianne, Île aux Aigrettes et Île aux Mangénie. Snorkeling sur l'Aigrettes, déjeuner BBQ sur l'Ilot Mangenie, et baignade finale à l'Île aux Cerfs — chaque étape organisée pour votre groupe uniquement.",
            seo5ilesH2b: "Le long de la côte Sud-Est de Maurice",
            seo5ilesPb: "La navigation longe la côte Sud-Est de Maurice depuis Trou d'Eau Douce : à cette période de l'année, il n'est pas rare de croiser des dauphins (observation non garantie). L'excursion inclut aussi la remontée jusqu'à la cascade de Grand River South East, l'une des plus belles chutes de l'île.",
            drinksLabel: "Boissons incluses",
            calcKicker: "Calculateur",
            calcTitle: "Composez votre tarif",
            paxLabel: "Nombre de personnes",
            baseIncluded: (n) => `Base ${n} pers. incluse`,
            extraNote: (price, n) => `Rs ${price}/personne au-delà de ${n}`,
            removeOne: "Retirer une personne",
            addOne: "Ajouter une personne",
            addLunch: "Ajouter le déjeuner",
            perPerson: "/ personne",
            lunchPackage: "Package déjeuner",
            included: "Inclus",
            premiumDesc:
                "Poisson BBQ, poulet BBQ, saucisses BBQ, salades, pain, dessert + langouste.",
            classicDesc:
                "Poisson BBQ, poulet BBQ, saucisses BBQ, salades, pain et dessert. Menu végétarien disponible sur demande.",
            totalLabel: "Total estimé",
            totalDisclaimer: "Prix indicatif — validé avant confirmation",
            cta: "Demander cette offre",
            redirectNote: "Redirige vers WhatsApp",
            waIntro: (name) =>
                `Bonjour Exotik Trips, je souhaite réserver l'excursion « ${name} ».`,
            waPax: (n) => `• Nombre de personnes : ${n}`,
            waLunchPremium: "• Déjeuner : Package Premium (avec langouste)",
            waLunchClassicIncluded: "• Déjeuner : Package Classic (inclus)",
            waLunchClassic: "• Déjeuner : Package Classic",
            waLunchNo: "• Déjeuner : non",
            waTotal: (total) => `• Total estimé : Rs ${total}`,
        },
        testimonials: {
            kicker: "Ils nous font confiance",
            titleA: "Histoires",
            titleB: " mauriciennes.",
        },
        seoHome: {
            title: "Excursions en bateau dans l'Est de Maurice | Exotik Trips",
            description:
                "Excursions en speedboat, sorties pêche et parachute ascensionnel au départ de Trou d'Eau Douce. 5 îles de l'Est, cascade de Grand River, lagon turquoise.",
        },
    },

    en: {
        nav: {
            accueil: "Home",
            experiences: "Experiences",
            about: "About",
            offres: "Our offers",
            addons: "Add-ons",
            contact: "Contact",
            quote: "Free quote",
        },
        hero: {
            overline: "High-end experience concierge",
            titleA: "Experience Mauritius",
            titleB: "differently",
            side: "Mauritius Island — Est. 2026",
            subtitle:
                "Speedboat trips, fishing outings and parasailing from Trou d'Eau Douce. The East islands, lagoons and waterfalls, organised for you, like for no one else.",
            ctaPrimary: "Plan my experience",
            ctaSecondary: "See the experiences",
            scroll: "Discover the experience",
            waMessage:
                "Hello Exotik Trips, I would like to plan an experience in Mauritius.",
            bgAlt: "Mauritius lagoon at sunset",
        },
        experiences: {
            kicker: "— Catalogue",
            titleA: "Sea outings designed ",
            titleB: "for you.",
            intro: "Private boat trips, fishing, parasailing: choose your day departing from Trou d'Eau Douce, we take care of the rest.",
            cardKicker: "Flagship trip · East of Mauritius",
            cardTitle: "Boat trips",
            cardText: "5 islands of the East, fishing trip, parasailing: departing from Trou d'Eau Douce.",
            flagLabel: "Flagship excursion",
            flagLine: "5 islands of the East by private boat: Rs 18,000 for 4 people, lunch included.",
            cta: "View the excursions",
        },
        about: {
            kicker: "About",
            titleA: "Mauritius,",
            titleB: "the secret side",
            brand: "Exotik Trips Moris",
            p1: " helps you discover Mauritius differently through unique excursions, sea experiences and activities tailored to your wishes.",
            p2: "Thanks to our partners, we offer a carefully chosen selection of activities and experiences to make your stay unforgettable.",
            p3: "From your arrival at the airport to your accommodation, we support you to make your stay simple, comfortable and unforgettable.",
            p4: "We also offer excursion packs, activity guides and an essential groceries service prepared before your arrival, so you can fully enjoy your holiday.",
            tags: [
                "Excursions",
                "Airport transfers",
                "Accommodation",
                "Pre-arrival groceries",
                "Local guides",
            ],
        },
        whyus: {
            kicker: "Why choose us",
            titleA: "Luxury is",
            titleB: "thinking of nothing",
            intro: "Exotik Trips, much more than an agency: a concierge service designed around you. Every detail is imagined, coordinated and anticipated so your only mission is to enjoy.",
            quote: "“The experience begins long before you arrive.”",
            reasons: [
                {
                    id: "premium",
                    title: "Premium",
                    text: "Hand-picked providers, high-end equipment, finishes cared for down to the last detail.",
                },
                {
                    id: "local",
                    title: "100% Local",
                    text: "We live in Mauritius. Secret spots, direct contacts, know-how passed down generation after generation.",
                },
                {
                    id: "stressfree",
                    title: "Stress-free",
                    text: "We coordinate everything — you enjoy. Plan B included, weather monitored, a human at the end of the line.",
                },
                {
                    id: "surmesure",
                    title: "Tailor-made",
                    text: "Every experience is designed around your wishes, your pace, your story.",
                },
            ],
        },
        offers: {
            kicker: "Our offers",
            titleA: "Three ways to",
            titleB: "experience",
            titleC: "Mauritius.",
            intro: "Choose your level of support. Activities are paid directly to the providers.",
            perOrder: "/order",
            cta: "Request this offer",
            disclaimer:
                "Displayed rates cover concierge fees. Services are invoiced separately by the partners.",
            waMessage: (name, price) =>
                `Hello, I am interested in the ${name} offer (${price}).`,
        },
        addons: {
            kicker: "Personalise",
            titleA: "The details that",
            titleB: "make the magic",
            intro: "Select your add-ons, we orchestrate the rest. Champagne at sunset, a surprise cake on the beach, a photographer to freeze the moment — the final touch that turns a moment into a memory.",
            selection: "Your selection",
            empty: "Choose your add-ons",
            count: (n) => `${n} item${n > 1 ? "s" : ""} added`,
            cta: "Add to my experience →",
            waEmpty:
                "Hello Exotik Trips, I would like to personalise my experience with add-ons.",
            waWith: (names) =>
                `Hello Exotik Trips, I would like to add to my experience: ${names}.`,
        },
        ctaband: {
            kicker: "Mauritius is waiting for you",
            titleA: "What if tomorrow",
            titleB: "were",
            titleC: "unforgettable",
            cta: "Plan my experience",
            waMessage: "Hello, I want to plan my experience in Mauritius.",
        },
        contact: {
            kicker: "Contact",
            titleA: "Let's talk about your",
            titleB: "next escape",
            whatsappLabel: "WhatsApp — the fastest",
            phoneLabel: "Phone",
            emailLabel: "Email",
            delayLabel: "Response time",
            delayValue: "Within 24h, Monday to Saturday",
            badge: "Reply within 24h",
            waMessage:
                "Hello Exotik Trips, I would like to discuss an experience.",
            nameLabel: "Full name *",
            namePh: "Your name",
            emailLabel: "Email *",
            emailPh: "you@email.com",
            phoneLabel: "Phone",
            phonePh: "+230 or other",
            experienceLabel: "Desired experience",
            choose: "— Choose —",
            messageLabel: "Your message",
            messagePh: "Dates, number of people, special wishes…",
            replyNoteA: "Fast reply within ",
            replyNoteB: "24h",
            submit: "Send my request",
            sending: "Sending…",
            expOptions: [
                "5 East Islands excursion",
                "Fishing trip",
                "Parasailing",
                "Other request",
            ],
            successKicker: "Request received",
            successTitleA: "Your request has been",
            successTitleB: "received.",
            successText:
                "We will get back to you within 24h directly by WhatsApp or email. For a faster reply, feel free to write to us directly.",
            successWa: "Write on WhatsApp",
            successWaMessage:
                "Hello, I sent a request via the website form.",
            successNew: "New request",
            errorRequired: "Name and email are required",
            errorGeneric:
                "Something went wrong. Please try again or use WhatsApp.",
        },
        footer: {
            description:
                "High-end experience concierge in Mauritius. From the turquoise lagoon to the mountains of the South, we orchestrate your escape.",
            explore: "Explore",
            contact: "Contact",
            legal: "Exotik Trips acts as a concierge service and intermediary. Services are performed by independent partners. Activities are paid directly to the providers.",
            rights: "All rights reserved",
            madeIn: "Made with love · Mauritius",
            admin: "Espace Pro",
        },
        waFloat: {
            label: "Chat with us",
            aria: "Chat with us on WhatsApp",
            message:
                "Hello Exotik Trips, I would like to plan an experience.",
        },
        boat: {
            modalKicker: "Boat trips",
            modalTitleA: "The",
            modalTitleB: "turquoise",
            modalTitleC: "lagoon, in all its forms.",
            close: "Close",
            backHome: "Back to home",
            pageKicker: "Boat trips · East of Mauritius",
            tabsAria: "Select an excursion",
            factDeparture: "Departure",
            factDuration: "Duration",
            factEnd: "Ends",
            factBase: "Base",
            pax: "pax",
            yourDay: "Your day",
            seo5ilesH2a: "The 5 East islands of Mauritius from Trou d'Eau Douce",
            seo5ilesPa: "A day on a private boat to explore the five jewels of the East lagoon: Île au Phare (Île aux Fouquets), Île de la Passe, Île aux Marianne, Île aux Aigrettes and Île aux Mangénie. Snorkelling at Aigrettes, BBQ lunch on Ilot Mangenie, and a final swim at Île aux Cerfs — every step organised for your group only.",
            seo5ilesH2b: "Along the South-East coast of Mauritius",
            seo5ilesPb: "The cruise follows the South-East coast of Mauritius from Trou d'Eau Douce: at this time of year, spotting dolphins is not uncommon (sighting not guaranteed). The trip also includes the ride up to the Grand River South East waterfall, one of the island's most beautiful falls.",
            drinksLabel: "Drinks included",
            calcKicker: "Calculator",
            calcTitle: "Build your price",
            paxLabel: "Number of people",
            baseIncluded: (n) => `Base of ${n} pax included`,
            extraNote: (price, n) => `Rs ${price}/person beyond ${n}`,
            removeOne: "Remove one person",
            addOne: "Add one person",
            addLunch: "Add lunch",
            perPerson: "/ person",
            lunchPackage: "Lunch package",
            included: "Included",
            premiumDesc:
                "BBQ fish, BBQ chicken, BBQ sausages, salads, bread, dessert + lobster.",
            classicDesc:
                "BBQ fish, BBQ chicken, BBQ sausages, salads, bread and dessert. Vegetarian menu available on request.",
            totalLabel: "Estimated total",
            totalDisclaimer: "Indicative price — confirmed before booking",
            cta: "Request this offer",
            redirectNote: "Redirects to WhatsApp",
            waIntro: (name) =>
                `Hello Exotik Trips, I would like to book the "${name}" trip.`,
            waPax: (n) => `• Number of people: ${n}`,
            waLunchPremium: "• Lunch: Premium package (with lobster)",
            waLunchClassicIncluded: "• Lunch: Classic package (included)",
            waLunchClassic: "• Lunch: Classic package",
            waLunchNo: "• Lunch: no",
            waTotal: (total) => `• Estimated total: Rs ${total}`,
        },
        testimonials: {
            kicker: "They trust us",
            titleA: "Mauritian",
            titleB: " stories.",
        },
        seoHome: {
            title: "Boat Trips in the East of Mauritius | Exotik Trips",
            description:
                "Speedboat trips, fishing outings and parasailing from Trou d'Eau Douce. 5 islands of the East, Grand River waterfall, turquoise lagoon.",
        },
    },
};

/** Current language + helpers, derived from the URL (/en prefix = English). */
export function useLang() {
    const { pathname } = useLocation();
    const isEn = pathname === "/en" || pathname.startsWith("/en/");
    const lang = isEn ? "en" : "fr";
    return {
        lang,
        isEn,
        t: I18N[lang],
        prefix: isEn ? "/en" : "",
        pathname,
    };
}

/** Merge an item's `en` block over its French base when English is active. */
export function loc(item, lang) {
    return lang === "en" && item.en ? { ...item, ...item.en } : item;
}

/** Rs 18 000 (fr) vs Rs 18,000 (en). */
export function fmtRs(n, lang) {
    return n.toLocaleString(lang === "en" ? "en-GB" : "fr-FR");
}
